import { DataField } from "./DataField.js";
import * as dtt from "../../data/model/data-types.js";

export class DataModel {

    constructor(authManager, dataEndpointPath, codelistEndpointPath = null, options = {}) {

        if (!authManager)
            throw new Error('[DataModel]: "authManager" is required');
        if (!dataEndpointPath)
            throw new Error('[DataModel]: "dataEndpointPath" is required');

        this.authManager = authManager;
        this.dataEndpointPath = dataEndpointPath;
        this.codelistEndpointPath = codelistEndpointPath;
        this.codelistValueLoader = options?.codelistValueLoader ?? null;

        this.entityName = null;
        this.label = 'Data Model';
        this.setLabel = 'Data Models'

        this._initialized = true;
        this._prepared = false;
        this._fieldsPrepared = false;

        this.fieldDefs = [];
        this.privileges = [];

        this.primaryKeyField = null;
        this.titleField = null;

    };

    async prepare() {

        if (this._prepared)
            return true;

        await Promise.all([

            this.prepareFields(),

        ]);

        this._prepared = true;

        return true;

    };

    isPrepared() { return this._prepared; };

    async prepareFields() {

        if (this._fieldsPrepared)
            return true;

        var codelistsToLoad = [];

        let loadPromises = [];

        this.fields = (this.getFieldDefs() || []).map((fieldDef, index) => {

            var field = new DataField(fieldDef);

            if (field.isTitle)
                this.titleField = field;
            if (field.isPrimaryKey)
                this.primaryKeyField = field;

            let type = dtt.getTypeById(field.datatypeId);

            if (dtt.isCodelistType(type)) {
                if (type.valuesEndpoint && !codelistsToLoad.includes(type.name)) {
                    loadPromises.push(dtt.loadTypeValues(type, this.getCodelistValueLoader()));
                    codelistsToLoad.push(type.name);
                }
            }

            return field;

        });

        if (loadPromises.length == 0) {

            this._fieldsPrepared = true;
            return true;

        }

        await Promise.all(loadPromises);

        this.fields.forEach((field) => {
            if (dtt.isCodelistType(field.datatype)) {
                field.availableValues = () => dtt.getTypeValues(field.datatype);
                field.formatter = dtt.getCodelistFormatter(field.datatype, field.availableValues);
            }
        });

        this._fieldsPrepared = true;

        return true;

    };

    // --------------------------------------------------------------------------

    getEntityName() { return this.entityName; }

    getLabel() { return this.label; }

    getSetLabel() { return this.setLabel; }

    getDataEndpointPath() { return this.dataEndpointPath; };

    getCodelistEndpointPath() { return this.codelistEndpointPath; };

    setCodelistValueLoader(codelistValueLoader) {

        if (codelistValueLoader !== null && typeof codelistValueLoader !== 'function')
            throw new Error('[DataModel]: "codelistValueLoader" must be a function or null.');

        this.codelistValueLoader = codelistValueLoader;
        return this;

    };

    getCodelistValueLoader() { return this.codelistValueLoader; };

    // --------------------------------------------------------------------------

    getPrivileges() {

        return this.privileges;

    };

    getFields() {

        return this.fields;

    };

    getFieldDefs() {

        return this.fieldDefs;

    };

    getFilterableFields() {

        return (this.getFields() || []).filter(field => field.filterable);

    };

    getDisplayableFields() {

        return (this.getFields() || []).filter(field => dtt.isValueType(field.type));

    };

    getEditableFields() {

        return (this.getFields() || []).filter(field => field.inEditor && !field.readOnly);

    };

    getPrimaryKeyField() {

        return this.primaryKeyField;

    };

    getPrimaryKey(data) {

        if (!this.primaryKeyField)
            return null;
        return this.primaryKeyField.getValue(data);

    };

    getTitleField() {

        return this.titleField;

    };

    getTitle(data) {

        if (this.titleField)
            return this.titleField.getValue(data);
        if (this.primaryKeyField)
            return '#' + this.primaryKeyField.getValue(data);
        return '?';

    };

    getReferencedCodelists() {

        var that = this;
        return (this.getFeildDefs() || []).reduce(function (result, def) {

            var datatype = dtt.getTypeByName(def.type);
            if (dtt.isCodelistType(datatype)) {
                result.push(datatype.valuesEndpoint);
            }
            return result;

        }, []);

    };

    // --------------------------------------------------------------------------

    createDataFilter(filter) {

        var dataFilter = '$filter=';
        var keys = Object.keys(filter);
        for (var i = 0; i < keys.length; i++) {
            if (i > 0)
                dataFilter = dataFilter + ' and ';
            var value = filter[keys[i]];
            switch (typeof (value)) {
                case 'string':
                    value = "'" + value + "'";
                    break;
            };
            dataFilter = dataFilter + keys[i] + ' eq ' + value;
        }
        return dataFilter;

    };

    // --------------------------------------------------------------------------

    createItem() {

        let item = {};

        this.getFields().forEach((field) => {
            item[field.name] = null;
        });

        return item;

    };

    serializeObjectForWire(data) {

        return this.mapObjectForTransport(data, (field, value) => field.serializeWireValue(value));

    };

    deserializeObjectFromWire(data) {

        return this.mapObjectForTransport(data, (field, value) => field.deserializeWireValue(value));

    };

    serializeDatasetForWire(dataset) {

        if (Array.isArray(dataset))
            return dataset.map((item) => this.serializeObjectForWire(item));
        if (!dataset || typeof dataset != 'object')
            return dataset;
        if (!Array.isArray(dataset.items))
            return dataset;

        return Object.assign({}, dataset, {
            items: dataset.items.map((item) => this.serializeObjectForWire(item)),
        });

    };

    deserializeDatasetFromWire(dataset) {

        if (Array.isArray(dataset))
            return dataset.map((item) => this.deserializeObjectFromWire(item));
        if (!dataset || typeof dataset != 'object')
            return dataset;
        if (!Array.isArray(dataset.items))
            return dataset;

        return Object.assign({}, dataset, {
            items: dataset.items.map((item) => this.deserializeObjectFromWire(item)),
        });

    };

    mapObjectForTransport(data, mapper) {

        if (!data || typeof data != 'object')
            return data;

        const fields = this.getFields() || [];
        const result = {};

        Object.getOwnPropertyNames(data).forEach((propName) => {
            if (propName.slice(0, 1) == '$')
                return;

            const field = fields.find((item) => item?.name == propName) || null;
            const value = data[propName];

            result[propName] = field ? mapper(field, value) : value;
        });

        return result;

    };

    // --------------------------------------------------------------------------

    getDataErrors(data) {

        return this.getFields().reduce((errors, field) => {
            let fieldErrors = field.getValueError(data);
            if (fieldErrors && fieldErrors.length)
                return errors.concat(fieldErrors);
            return errors;
        });

    };

    validateData(data) {

        return this.getFields().every((field) => field.validateValue(data));

    };

}
